import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ButtonsSection from './buttons-section/buttons-sections.component';
import PrimaryLocationSection from './related-location-section/primary-location-section/primary-location-section.component';
import SecondaryLocationSection from './related-location-section/secondary-location-section/secondary-location-section.component';
import IncDrawer, { IncDrawerType } from '../inc-drawer/inc-drawer.component';
import UtilityDrawerBody from '../utility-drawer/utility-drawer-body/utility-drawer-body.component';
import { StoreResult } from 'api/client-domain/client-domain.types';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';
import { useGetStoreOrderingSettingQuery } from 'api/ordering-service/ordering-service';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useEffect } from 'react';

export type RelatedLocationsDrawerDataType = {
  locationId: string;
  store?: StoreResult;
  ordering?: StoreOrderingForm;
};

type RelatedLocationsDrawerProps =
  IncDrawerType<RelatedLocationsDrawerDataType>;

const RelatedLocationsDrawer = ({
  isOpen,
  close,
  data: drawerData,
  ...drawerRest
}: RelatedLocationsDrawerProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, isFetching, refetch } = useGetStoreOrderingSettingQuery(
    drawerData?.locationId ?? skipToken,
  );
  const { primaryLocation, primaryStore, relatedStores } = data ?? {};

  const onNavigate = (relatedLocationId: string) => {
    close();
    navigate(`/locations/${relatedLocationId}`);
  };

  const isPrimaryLocation = !!primaryLocation;
  const hasRelatedLocations = !isFetching && !!relatedStores?.length;

  useEffect(() => {
    if (isOpen && drawerData?.locationId) refetch();
  }, [drawerData?.locationId, isOpen, refetch]);

  return (
    <IncDrawer
      isOpen={isOpen}
      close={close}
      data={{ ...drawerData, ordering: data }}
      data-testid="relatedLocationsDrawer.Drawer"
      title={t('drawer.relatedLocationsDrawer.title')}
      subtitle={
        isPrimaryLocation
          ? t('drawer.relatedLocationsDrawer.subtitle.primaryLocation')
          : t('drawer.relatedLocationsDrawer.subtitle.secondaryLocation')
      }
      isLoading={isFetching}
      {...drawerRest}
    >
      <UtilityDrawerBody>
        {isPrimaryLocation && !hasRelatedLocations ? (
          <Typography gutterBottom color="text.secondary" textAlign="center">
            {t('drawer.relatedLocationsDrawer.contentWithoutRelatedLocations')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {!isPrimaryLocation && (
              <PrimaryLocationSection
                primaryLocation={primaryStore}
                onNavigate={onNavigate}
              />
            )}
            {hasRelatedLocations && (
              <SecondaryLocationSection
                secondaryLocations={relatedStores}
                onNavigate={onNavigate}
              />
            )}
          </Stack>
        )}
      </UtilityDrawerBody>
      <ButtonsSection relatedLocation={{ ...drawerData, ordering: data }} />
    </IncDrawer>
  );
};

export default RelatedLocationsDrawer;
